import { environmentURLs } from "../utils/environmentURLs";
import { readAnalyticsContext } from "../lib/funnel";
const { frontendAPIUrl } = environmentURLs;

// Journeys (additive): attach the optional signup/login `analytics` context as an EXTRA, LAST key.
// When the context is unavailable, throws, or is empty ({}), the original object is returned
// untouched so the request body is byte-for-byte what it was before.
const withAnalyticsContext = (body) => {
  try {
    const analytics = readAnalyticsContext();
    if (analytics && Object.keys(analytics).length > 0) {
      return { ...body, analytics };
    }
  } catch (_) {
    /* analytics must never break auth */
  }
  return body;
};

const handleUserLogin = async (email, password, AuthInfo) => {
  const requestBody = {
    email: email,
    password: password,
    client_id: AuthInfo ? AuthInfo.client_id : null,
    redirect_uri: AuthInfo ? AuthInfo.redirect_uri : null,
    state: AuthInfo ? AuthInfo.state : null,
  };
  try {
    const response = await fetch(
      `${frontendAPIUrl}/login?authorization_type=${
        AuthInfo ? AuthInfo.authorization_type : "default"
      }`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(withAnalyticsContext(requestBody)),
      }
    );

    const result = await response.json();
    console.log("result is", result);
    if (result.error) {
      throw new Error(result.authFlag || "internalServerError");
    }
    return { data: result.data };
  } catch (thrownError) {
    console.error("An error occurred:", thrownError);
    throw thrownError;
  }
};

const handleUserRegistration = async (name, email, password, redirectTo) => {
  try {
    const response = await fetch(`${frontendAPIUrl}/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(
        withAnalyticsContext({
          name: name,
          email: email,
          password: password,
          redirectTo,
        })
      ),
    });

    const result = await response.json();
    if (result.error) {
      throw new Error(result.authFlag || "internalServerError");
    }

    return { data: result.data };
  } catch (thrownError) {
    console.error("An error occurred:", thrownError);
    throw thrownError;
  }
};

const generateMagicLink = async (params, AuthInfo) => {
  const { email, name, source, redirectTo } = params;

  // Only include OAuth2 parameters if authorization_type is oauth2
  const requestBody = {
    email: email,
    name: name,
    redirectTo: redirectTo,
    source: source,
  };

  // Add OAuth2 parameters only if AuthInfo exists and authorization_type is oauth2
  if (AuthInfo && AuthInfo.authorization_type === 'oauth2') {
    requestBody.client_id = AuthInfo.client_id;
    requestBody.redirect_uri = AuthInfo.redirect_uri;
    requestBody.state = AuthInfo.state;
  }

  try {
    const response = await fetch(
      `${frontendAPIUrl}/login/generate-magic-link?authorization_type=${
        AuthInfo ? AuthInfo.authorization_type : "default"
      }`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(withAnalyticsContext(requestBody)),
      }
    );

    const result = await response.json();
    console.log("result is: ", result.error);
    if (result.error) {
      if (result.error === "Name is required for new user registration") {
        throw new Error("Name is required for new user registration");
      }
      throw new Error(result.authFlag || "internalServerError");
    }

    return true;
  } catch (thrownError) {
    console.error("An error occurred:", thrownError);
    throw thrownError;
  }
};

async function verifyToken(token, id) {
  const requestOptions = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-access-token": token,
    },
  };
  try {
    const response = await fetch(
      `${frontendAPIUrl}/user/${id}/`,
      requestOptions
    );

    const result = await response.json();
    if (result.error) {
      throw new Error(result.authFlag || "internalServerError");
    }
    const user = result.data;
    return user;
  } catch (thrownError) {
    console.error("An error occurred:", thrownError);
    throw thrownError;
  }
}

const verifyMagicLink = async (token, authType, authInfo) => {
  if (!token) {
    throw new Error("Invalid token. Please try again.");
  }

  // Only include state in request body if authInfo exists and has a state
  const requestBody = { token: token };
  if (authInfo && authInfo.state) {
    requestBody.state = authInfo.state;
  }

  try {
    const response = await fetch(
      `${frontendAPIUrl}/login/verify-magic-link?authorization_type=${
        authType || "default"
      }`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    const result = await response.json();
    if (result.error) {
      throw new Error(result.authFlag || "internalServerError");
    }

    return { data: result.data };
  } catch (thrownError) {
    console.error("An error occurred:", thrownError);
    throw thrownError;
  }
};

const validateAndProcessToken = async (token, AuthInfo) => {
  try {
    const response = await fetch(
      `${frontendAPIUrl}/login/auth/google?authorization_type=${
        AuthInfo ? AuthInfo.authorization_type : "default"
      }`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          withAnalyticsContext({
            ...token,
            client_id: AuthInfo ? AuthInfo.client_id : undefined,
            redirect_uri: AuthInfo ? AuthInfo.redirect_uri : token.redirect_uri,
            state: AuthInfo ? AuthInfo.state : undefined,
          })
        ),
      }
    );

    const result = await response.json();
    if (result.error) {
      throw new Error(result.authFlag || "internalServerError");
    }

    return { data: result.data };
  } catch (thrownError) {
    console.error("An error occurred:", thrownError);
    throw thrownError;
  }
};

export {
  handleUserLogin,
  handleUserRegistration,
  generateMagicLink,
  verifyMagicLink,
  validateAndProcessToken,
  verifyToken,
};
